import React from 'react';
import {
  Search, Plus, Package, ScanBarcode, X, Fuel
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

export const POSProductGrid = ({
  // Search & Scan
  searchQuery, handleSearch, scannerMode, setScannerMode,
  barcodeInput, setBarcodeInput, handleBarcodeScanned,
  // Quick Add & Fuel
  setShowQuickAddDialog, selectedIndustry, setShowFuelPrepayDialog,
  // Quick Keys
  quickKeys, handleQuickKeyClick,
  // Products
  products, viewMode, gridSize, GRID_SIZES, addToCart, getStockStatus,
  // Sample products
  businessType, loadSampleProducts,
}) => (
  <div className="flex-1 flex flex-col min-w-0">
    {/* Search & Controls */}
    <div className="flex gap-2 mb-3">
      <Button
        variant={scannerMode ? 'default' : 'outline'}
        className={`h-12 px-4 gap-2 shrink-0 ${scannerMode ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}
        onClick={() => { setScannerMode(!scannerMode); setBarcodeInput(''); }}
        data-testid="scan-mode-toggle"
      >
        <ScanBarcode className="h-5 w-5" />
        <span className="hidden sm:inline">{scannerMode ? 'Scan ON' : 'Scan'}</span>
      </Button>

      {scannerMode ? (
        <div className="relative flex-1">
          <ScanBarcode className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-emerald-500" />
          <Input
            placeholder="Scan barcode or type UPC and press Enter..."
            value={barcodeInput}
            onChange={(e) => setBarcodeInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && barcodeInput.trim()) handleBarcodeScanned(barcodeInput.trim());
            }}
            className="pl-12 h-12 text-lg border-emerald-500 ring-1 ring-emerald-500/30 bg-emerald-50 dark:bg-emerald-950/20"
            autoFocus
            data-testid="scan-mode-input"
          />
          {barcodeInput && (
            <Button variant="ghost" size="icon" className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8"
              onClick={() => setBarcodeInput('')}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      ) : (
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input placeholder="Search products or scan barcode..." value={searchQuery}
            onChange={handleSearch} className="pl-12 h-12 text-lg" autoFocus />
        </div>
      )}

      <Button onClick={() => setShowQuickAddDialog(true)} className="h-12 bg-blue-600 hover:bg-blue-700 gap-2"
        data-testid="quick-add-product-btn">
        <Plus className="h-5 w-5" />
        <span className="hidden lg:inline">Quick Add</span>
      </Button>

      {selectedIndustry === 'gas_station' && (
        <Button onClick={() => setShowFuelPrepayDialog(true)} className="h-12 bg-orange-500 hover:bg-orange-600 gap-2"
          data-testid="fuel-prepay-btn">
          <Fuel className="h-5 w-5" />
          <span className="hidden lg:inline">Fuel Prepay</span>
        </Button>
      )}
    </div>

    {/* Scan Mode Banner */}
    {scannerMode && (
      <div className="mb-3 p-3 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-lg flex items-center gap-3" data-testid="scan-mode-banner">
        <div className="flex items-center gap-2">
          <div className="h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">Scan Mode Active</span>
        </div>
        <span className="text-xs text-emerald-600 dark:text-emerald-400">Scan any barcode to instantly add to cart. Unknown barcodes open Quick Add.</span>
      </div>
    )}

    {/* Quick Keys */}
    {quickKeys.length > 0 && (
      <div className="mb-3">
        <div className="flex gap-1.5 flex-wrap">
          {quickKeys.map(qk => (
            <Button key={qk.id} variant="outline"
              className="h-9 px-3 flex items-center gap-1.5 border hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-950 transition-all text-xs"
              onClick={() => handleQuickKeyClick(qk)} data-testid={`quick-key-${qk.id}`}>
              <span className="text-base">{qk.icon}</span>
              <span className="font-medium text-slate-700 dark:text-slate-200">{qk.name}</span>
            </Button>
          ))}
        </div>
      </div>
    )}

    {/* Products Grid */}
    <div className="flex-1 overflow-y-auto">
      {viewMode === 'grid' ? (
        <div className={`grid ${GRID_SIZES[gridSize].gridCols} ${GRID_SIZES[gridSize].gap}`}>
          {products.map(product => {
            const stockStatus = getStockStatus(product);
            const sizeConfig = GRID_SIZES[gridSize];
            return (
              <Card key={product.id}
                className={`cursor-pointer hover:ring-2 hover:ring-primary hover:shadow-lg transition-all active:scale-95 ${
                  stockStatus.status === 'out' ? 'opacity-60' : ''
                }`}
                onClick={() => {
                  if (stockStatus.status === 'out') { toast.error(`${product.name} is out of stock!`); return; }
                  addToCart(product);
                  if (stockStatus.status === 'low') toast.warning(`Low stock alert: ${stockStatus.label}`);
                }}>
                <CardContent className={`${sizeConfig.padding} relative`}>
                  {stockStatus.status !== 'ok' && (
                    <div className={`absolute top-1 right-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${
                      stockStatus.status === 'out' ? 'bg-red-500 text-white' : 'bg-amber-500 text-white'
                    }`}>{stockStatus.status === 'out' ? 'OUT' : stockStatus.label}</div>
                  )}
                  <div className={`${sizeConfig.imageContainer} bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 rounded-md mb-1.5 flex items-center justify-center`}>
                    {product.image_url ? (
                      product.image_url.startsWith('icon:') ? (
                        <span className="text-lg">{product.image_url.replace('icon:', '')}</span>
                      ) : (
                        <img src={product.image_url} alt={product.name} className="h-full w-full object-cover rounded-md" />
                      )
                    ) : (
                      <Package className={`${sizeConfig.imageSize} text-muted-foreground/40`} />
                    )}
                  </div>
                  <p className={`font-semibold ${sizeConfig.nameSize} truncate`}>{product.name}</p>
                  <div className="flex items-center justify-between mt-0.5">
                    <p className={`${sizeConfig.priceSize} text-primary`}>
                      ${(product.sell_price || product.cost_price * 1.3).toFixed(2)}
                    </p>
                    <span className={`${sizeConfig.stockSize} px-1.5 py-0.5 rounded ${
                      stockStatus.status === 'ok' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300' :
                      stockStatus.status === 'low' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300' :
                      'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                    }`}>{product.stock_quantity || 0}</span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="space-y-2">
          {products.map(product => {
            const stockStatus = getStockStatus(product);
            return (
              <Card key={product.id}
                className={`cursor-pointer hover:ring-2 hover:ring-primary transition-all ${
                  stockStatus.status === 'out' ? 'opacity-60' : ''
                }`}
                onClick={() => {
                  if (stockStatus.status === 'out') { toast.error(`${product.name} is out of stock!`); return; }
                  addToCart(product);
                  if (stockStatus.status === 'low') toast.warning(`Low stock alert: ${stockStatus.label}`);
                }}>
                <CardContent className="p-3 flex items-center gap-4">
                  <div className="h-12 w-12 bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 rounded-lg flex items-center justify-center shrink-0">
                    {product.image_url ? (
                      product.image_url.startsWith('icon:') ? (
                        <span className="text-xl">{product.image_url.replace('icon:', '')}</span>
                      ) : (
                        <img src={product.image_url} alt={product.name} className="h-full w-full object-cover rounded-lg" />
                      )
                    ) : (
                      <Package className="h-6 w-6 text-muted-foreground/50" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{product.name}</p>
                    <p className="text-xs text-muted-foreground">{product.sku}</p>
                  </div>
                  <p className="text-lg font-bold text-primary">${(product.sell_price || product.cost_price * 1.3).toFixed(2)}</p>
                  <span className={`text-xs px-2 py-1 rounded ${
                    stockStatus.status === 'ok' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300' :
                    stockStatus.status === 'low' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300' :
                    'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                  }`}>{product.stock_quantity || 0}</span>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {products.length === 0 && (
        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
          <Package className="h-16 w-16 mb-4 opacity-30" />
          <p className="text-lg font-medium">No products found</p>
          <p className="text-sm mt-1">Try a different search or add products</p>
          <Button variant="outline" className="mt-4 gap-2" onClick={loadSampleProducts}
            data-testid="load-sample-products-btn">
            <Package className="h-4 w-4" /> Load Sample {businessType?.name || 'Retail'} Products
          </Button>
        </div>
      )}
    </div>
  </div>
);
